import { ITEM_FIELDS } from '@/graphql/queries/items';
import { MANIFEST_FIELDS } from '@/graphql/queries/manifests';
import { INBOUND_ITEM_WAREHOUSE_FIELDS } from '@/graphql/queries/manifests_inbound_items';
import { ORDER_FIELDS } from '@/graphql/queries/orders';
import { WAREHOUSE_EVENT_FIELDS } from '@/graphql/queries/order_wh_events';
import { PALLET_ITEM_FIELDS } from '@/graphql/queries/pallet_items';
import { ROUTE_MANIFEST_FIELDS } from '@/graphql/queries/routes';
import { MANIFEST_SUMMARY_VIEW_FIELDS } from '@/graphql/fragments/manifest_summary_view';
import { TRACKING_EVENT_FIELDS } from '@/graphql/fragments/tracking_events';
import { gql } from '@apollo/client';

// Full item row — fetched on demand for partial receive so the split can clone a complete remainder
// row (the manifest detail query only loads a warehouse subset of item fields).
export const GET_ITEM = gql`
    ${ITEM_FIELDS}
    query GetItem($item_id: uuid!) {
        items_by_pk(item_id: $item_id) {
            ...ItemFields
        }
    }
`;

export const GET_MANIFEST = gql`
    ${MANIFEST_FIELDS}
    ${INBOUND_ITEM_WAREHOUSE_FIELDS}
    ${ROUTE_MANIFEST_FIELDS}
    ${MANIFEST_SUMMARY_VIEW_FIELDS}
    query GetManifest($manifest_id: uuid!) {
        manifest: manifests_by_pk(manifest_id: $manifest_id) {
            ...ManifestFields
            items {
                ...InboundItemWarehouseFields
            }
            route {
                ...RouteManifestFields
            }
            warehouse_summary {
                ...ManifestSummaryViewFields
            }
        }
        # Sibling-item lists fetched once per order (not once per manifest item — see the comment on
        # InboundItemWarehouseFields) and stitched back onto items[].item.order.itemsByOrderId in
        # components/Manifest/store.js.
        orders(where: { itemsByOrderId: { manifests: { manifest_id: { _eq: $manifest_id } } } }) {
            order_id
            order_type
            forward_stocked_status
            itemsByOrderId {
                item_id
                sku
                description
                item_type_details
                carton_quantity
                quantity
                length
                width
                height
                total_weight
                intangible
                is_return
                pallet_id
                pallet {
                    pallet_id
                    warehouse_status
                }
                inventory_unit {
                    received_at
                    closed_at
                }
                latest_event: tracking_events(
                    order_by: [{ event_time: desc_nulls_last }, { event_id: desc }]
                    limit: 1
                ) {
                    event_id
                    event_type
                }
            }
        }
    }
`;

// LOAD_FEEDBACK questions for the manifest's shipper and carrier; resolveLoadFeedbackQuestions
// picks which set applies.
export const GET_LOAD_FEEDBACK_QUESTIONS = gql`
    query LoadFeedbackQuestions($client_ids: [uuid!]!) {
        survey_questions(
            where: { client_id: { _in: $client_ids }, survey_type: { _eq: "LOAD_FEEDBACK" }, enabled: { _eq: true } }
        ) {
            survey_id
            client_id
            question
            response_type
        }
    }
`;

// Manifest-scoped survey answers upsert (targets the survey_id+manifest_id unique constraint).
export const UPSERT_MANIFEST_SURVEY_RESPONSES = gql`
    mutation UpsertManifestSurveyResponses($objects: [survey_responses_insert_input!]!) {
        insert_survey_responses(
            objects: $objects
            on_conflict: {
                constraint: survey_responses_survey_id_manifest_id_key
                update_columns: [response_string, response_boolean, response_number]
            }
        ) {
            affected_rows
        }
    }
`;

export const GET_RACKS = gql`
    query GetRacks($location_id: uuid!) {
        location: locations_by_pk(location_id: $location_id) {
            location_id
            warehouse_racks
        }
    }
`;

// Returning only ManifestFields: updateManifest's result is unused by its caller (Manifest/store.js
// has no onCompleted), and the full item→order→sibling-items graph in items/route duplicates once
// per manifest item, which blows Postgres's string buffer past 1GB on manifests with 1000+ items.
export const UPDATE_MANIFEST = gql`
    ${MANIFEST_FIELDS}
    mutation UpdateManifest($manifest_id: uuid!, $update: manifests_set_input!) {
        updated: update_manifests_by_pk(pk_columns: { manifest_id: $manifest_id }, _set: $update) {
            ...ManifestFields
        }
    }
`;

export const DELETE_MANIFEST = gql`
    mutation DeleteManifest($manifest_id: uuid!) {
        removed: delete_manifests_by_pk(manifest_id: $manifest_id) {
            manifest_id
        }
    }
`;

// Every item sitting on the given pallets — including items outside this manifest, so consolidation
// only deletes a pallet once nothing is left on it, and so a pallet shared with an order that is not
// on this manifest still reads as multi-order.
//
// Deliberately unlimited: a truncated list would show a shared pallet as single-order, and that error
// points toward the damage — staging it would strand the other orders' items.
export const GET_PALLET_ITEM_IDS = gql`
    query GetPalletItemIds($pallet_ids: [uuid!] = []) {
        items(where: { pallet_id: { _in: $pallet_ids } }) {
            item_id
            pallet_id
            order_id
        }
    }
`;

// Items on the given pallets with their orders' receive events — un-receive flips the whole pallet, so
// co-tenant orders have to be rolled back too, not just counted.
export const GET_PALLET_ITEM_ORDERS = gql`
    ${WAREHOUSE_EVENT_FIELDS}
    query GetPalletItemOrders($pallet_ids: [uuid!] = []) {
        items(where: { pallet_id: { _in: $pallet_ids } }) {
            item_id
            pallet_id
            inventory_unit {
                item_id
            }
            order {
                order_id
                order_number
                # Un-receive reaches every order on the pallet, so the forward-stocking gate has to be
                # evaluated against the co-tenants too, not just the clicked row.
                order_type
                forward_stocked_status
                wh_events(
                    order_by: [{ created_at: asc_nulls_first }, { transition: { current: asc } }, { event_id: asc }]
                ) {
                    ...WarehouseEvents
                }
            }
        }
    }
`;

export const INSERT_PALLET = gql`
    ${ITEM_FIELDS}
    ${WAREHOUSE_EVENT_FIELDS}
    ${TRACKING_EVENT_FIELDS}
    mutation InsertItemPallet(
        $item_id: uuid!
        $pallet: pallet_items_insert_input!
        $events: [order_wh_events_insert_input!] = []
        $trackingEvents: [tracking_events_insert_input!] = []
    ) {
        item: insert_items_one(
            object: { item_id: $item_id, pallet: { data: $pallet } }
            on_conflict: { constraint: items_pkey, update_columns: [pallet_id] }
        ) {
            ...ItemFields
        }
        events: insert_order_wh_events(
            objects: $events
            on_conflict: { constraint: order_wh_events_pkey, update_columns: [status, received_date] }
        ) {
            returning {
                ...WarehouseEvents
            }
        }
        tracking_events: insert_tracking_events(
            objects: $trackingEvents
            on_conflict: { constraint: tracking_events_uniq, update_columns: [] }
        ) {
            returning {
                ...TrackingEventFields
            }
        }
    }
`;

// Batched form of INSERT_PALLET: one round trip to give many items their own SINGLE pallet.
export const INSERT_ITEM_PALLETS_MANY = gql`
    ${ITEM_FIELDS}
    mutation InsertItemPallets($items: [items_insert_input!] = []) {
        items: insert_items(objects: $items, on_conflict: { constraint: items_pkey, update_columns: [pallet_id] }) {
            returning {
                ...ItemFields
            }
        }
    }
`;

export const INSERT_EMPTY_PALLET = gql`
    ${PALLET_ITEM_FIELDS}
    mutation InsertEmptyPallet($object: pallet_items_insert_input = {}) {
        insert_pallet_items_one(object: $object) {
            ...PalletItemFields
        }
    }
`;

export const UPDATE_PALLET = gql`
    ${PALLET_ITEM_FIELDS}
    ${WAREHOUSE_EVENT_FIELDS}
    ${TRACKING_EVENT_FIELDS}
    mutation UpdateItemPallet(
        $pallet_id: uuid!
        $update: pallet_items_set_input!
        $events: [order_wh_events_insert_input!] = []
        $trackingEvents: [tracking_events_insert_input!] = []
    ) {
        pallet: update_pallet_items_by_pk(pk_columns: { pallet_id: $pallet_id }, _set: $update) {
            ...PalletItemFields
        }
        events: insert_order_wh_events(
            objects: $events
            on_conflict: { constraint: order_wh_events_pkey, update_columns: [status, received_date] }
        ) {
            returning {
                ...WarehouseEvents
            }
        }
        tracking_events: insert_tracking_events(
            objects: $trackingEvents
            on_conflict: { constraint: tracking_events_uniq, update_columns: [] }
        ) {
            returning {
                ...TrackingEventFields
            }
        }
    }
`;

export const UPDATE_PALLETS_MANY = gql`
    ${PALLET_ITEM_FIELDS}
    ${TRACKING_EVENT_FIELDS}
    mutation UpdateItemPallets(
        $updates: [pallet_items_updates!]!
        $trackingEvents: [tracking_events_insert_input!] = []
    ) {
        updated: update_pallet_items_many(updates: $updates) {
            returning {
                ...PalletItemFields
            }
        }
        tracking_events: insert_tracking_events(
            objects: $trackingEvents
            on_conflict: { constraint: tracking_events_uniq, update_columns: [] }
        ) {
            returning {
                ...TrackingEventFields
            }
        }
    }
`;

// Patches the receive marker rather than deleting it, so the audit trail survives. warehouse_status
// derives from the latest non-null status; event_state derives from the action, so it stays put.
export const ROLLBACK_RECEIVE_EVENTS = gql`
    ${WAREHOUSE_EVENT_FIELDS}
    mutation RollbackReceiveEvents($event_ids: [uuid!] = []) {
        cleared: update_order_wh_events(
            where: { event_id: { _in: $event_ids } }
            _set: { status: "NOT_DELIVERED", received_date: null }
        ) {
            returning {
                ...WarehouseEvents
            }
        }
    }
`;

export const UPDATE_ORDERS = gql`
    ${ORDER_FIELDS}
    mutation UpdateOrders($order_ids: [uuid!]!, $update: orders_set_input!) {
        update_orders(where: { order_id: { _in: $order_ids } }, _set: $update) {
            affected_rows
            returning {
                ...OrderFields
            }
        }
    }
`;

export const UPDATE_ITEM_PALLETS_MANY = gql`
    ${ITEM_FIELDS}
    ${PALLET_ITEM_FIELDS}
    ${ORDER_FIELDS}
    mutation UpdateItemPallets(
        $itemUpdates: [items_updates!] = {}
        $palletUpdates: [pallet_items_updates!] = {}
        $palletsToDelete: [uuid!] = ""
    ) {
        update_items_many(updates: $itemUpdates) {
            affected_rows
            returning {
                ...ItemFields
            }
        }
        update_pallet_items_many(updates: $palletUpdates) {
            affected_rows
            returning {
                ...PalletItemFields
                items {
                    ...ItemFields
                    order {
                        ...OrderFields
                    }
                }
            }
        }
        delete_pallet_items(where: { pallet_id: { _in: $palletsToDelete }, _not: { items: {} } }) {
            affected_rows
        }
    }
`;
